/**
 * Lazy Loading Component
 * Handles lazy loading of images with intersection observer
 */

export class LazyLoading {
    private imageObserver: IntersectionObserver | null = null;
    private lazyImages: NodeListOf<HTMLImageElement>;

    constructor() {
        this.lazyImages = document.querySelectorAll('img[data-lazy-src]');
        this.init();
    }

    private init(): void {
        if (!('IntersectionObserver' in window)) {
            // Fallback for browsers without IntersectionObserver
            this.loadAllImages();
            return;
        }

        this.createObserver();
        this.observeImages();
    }

    private createObserver(): void {
        const options: IntersectionObserverInit = {
            root: null,
            rootMargin: '50px',
            threshold: 0.1
        };

        this.imageObserver = new IntersectionObserver((entries, observer) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const img = entry.target as HTMLImageElement;
                    this.loadImage(img);
                    observer.unobserve(img);
                }
            });
        }, options);
    }

    private observeImages(): void {
        if (!this.imageObserver) return;
        
        this.lazyImages.forEach(img => {
            this.imageObserver?.observe(img);
        });
    }

    private loadImage(img: HTMLImageElement): void {
        const lazySrc = img.dataset.lazySrc;
        if (!lazySrc) return;

        // Handle picture element with sources
        const picture = img.closest('picture');
        if (picture) {
            this.loadPictureElement(picture, img);
        } else {
            this.loadSimpleImage(img);
        }
    }

    private loadPictureElement(picture: HTMLPictureElement, img: HTMLImageElement): void {
        const sources = picture.querySelectorAll<HTMLSourceElement>('source[data-lazy-srcset]');
        const lazySrc = img.dataset.lazySrc;
        
        if (!lazySrc) return;

        // Create a new image to preload the main src
        const imageLoader = new Image();
        
        imageLoader.onload = () => {
            // Load all source elements
            sources.forEach((sourceElement) => {
                const source = sourceElement as HTMLSourceElement;
                const lazySrcset = source.dataset.lazySrcset;
                const lazySizes = source.dataset.lazySizes;
                if (lazySrcset) {
                    source.srcset = lazySrcset;
                    if (lazySizes) {
                        source.sizes = lazySizes;
                    }
                }
                // Remove data attributes to prevent re-processing
                delete source.dataset.lazySrcset;
                delete source.dataset.lazySizes;
            });
            
            // Load the main image
            img.src = lazySrc;
            img.classList.remove('animate-pulse', 'bg-gray-200');
            img.classList.add('loaded');
            
            // Remove data-lazy-src attribute to stop CSS animation
            delete img.dataset.lazySrc;
        };

        imageLoader.onerror = () => {
            img.classList.remove('animate-pulse', 'bg-gray-200');
            img.classList.add('error');
            
            // Remove data-lazy-src attribute even on error
            delete img.dataset.lazySrc;
        };

        imageLoader.src = lazySrc;
    }

    private loadSimpleImage(img: HTMLImageElement): void {
        const lazySrc = img.dataset.lazySrc;
        if (!lazySrc) return;

        // Create a new image to preload
        const imageLoader = new Image();
        
        imageLoader.onload = () => {
            img.src = lazySrc;
            img.classList.remove('animate-pulse', 'bg-gray-200');
            img.classList.add('loaded');
            
            // Remove data-lazy-src attribute to stop CSS animation
            delete img.dataset.lazySrc;
        };

        imageLoader.onerror = () => {
            img.classList.remove('animate-pulse', 'bg-gray-200');
            img.classList.add('error');
            
            // Remove data-lazy-src attribute even on error
            delete img.dataset.lazySrc;
        };

        imageLoader.src = lazySrc;
    }

    private loadAllImages(): void {
        // Fallback: load all images immediately
        this.lazyImages.forEach(img => {
            this.loadImage(img);
        });
    }

    /**
     * Public method to refresh lazy loading for new images
     */
    public refresh(): void {
        this.lazyImages = document.querySelectorAll('img[data-lazy-src]');
        this.observeImages();
    }

    /**
     * Public method to destroy the observer
     */
    public destroy(): void {
        if (this.imageObserver) {
            this.imageObserver.disconnect();
            this.imageObserver = null;
        }
    }
}

/**
 * Initialize lazy loading when DOM is ready
 */
export function initLazyLoading(): LazyLoading {
    return new LazyLoading();
}
